import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import "../../assets/scss/custom/components/page-widgets.scss";

import checkIcon from "../../assets/images/user-dashboard/check.svg";
import chevronDownIcon from "../../assets/images/support/chevron-down.svg";
import arrowRightIconAuth from "../../assets/images/authentication/arrow-right.svg";
import gameBlue from "../../assets/images/support/game-blue.svg";
import gameGreen from "../../assets/images/support/game-green.svg";
import gameRed from "../../assets/images/support/game-red.svg";
import gamePurple from "../../assets/images/support/game-purple.svg";

/* =========================================================
   Global page widgets — shared across Profile, Settings,
   Support, Referrals, Games (and any future account page).
   Import from here rather than re-building any of these.
   ========================================================= */

/* -------------------------------------------------------------------------
 * PageHeader — page title + optional primary action button, top of page.
 * ---------------------------------------------------------------------- */

export const PageHeader = ({ title, actionLabel, onAction, actionIcon }) => (
  <div className="page-header">
    <h1 className="page-header__title">{title}</h1>
    {actionLabel && (
      <button type="button" className="page-header__action" onClick={onAction}>
        {actionLabel}
        <span className="page-header__action-icon">
          <img src={actionIcon || arrowRightIconAuth} alt="" />
        </span>
      </button>
    )}
  </div>
);

PageHeader.propTypes = {
  title: PropTypes.string.isRequired,
  actionLabel: PropTypes.string,
  onAction: PropTypes.func,
  actionIcon: PropTypes.string,
};
PageHeader.defaultProps = { actionLabel: null, onAction: () => {}, actionIcon: null };

/* -------------------------------------------------------------------------
 * IconAvatar — small colored icon badge. One icon asset works for any
 * color: "tinted" masks the icon to a solid tone color on a soft tinted
 * background (used in tables); "solid" forces the icon white on a solid
 * tone background (used in stat cards). Shape can be circle or square.
 * ---------------------------------------------------------------------- */

const TONE_HEX = {
  blue: "#007BFF",
  green: "#1D9C87",
  red: "#FF3535",
  orange: "#EC421C",
  purple: "#6A3BB1",
  teal: "#0EA5A5",
  grey: "#8A8F98",
};

export const IconAvatar = ({ icon, tone, shape, variant, size }) => {
  const hex = TONE_HEX[tone] || TONE_HEX.grey;
  const wrapStyle = {
   
  };

  return (
    <span className={`icon-avatar icon-avatar--${shape}`} style={wrapStyle}>
      {variant === "solid" ? (
        <img src={icon} alt="" className="icon-avatar__img icon-avatar__img--white" />
      ) : (
        <span
          className="icon-avatar__glyph"
          style={{
            WebkitMaskImage: `url(${icon})`,
            maskImage: `url(${icon})`,
            backgroundColor: hex,
          }}
        />
      )}
    </span>
  );
};

IconAvatar.propTypes = {
  icon: PropTypes.string.isRequired,
  tone: PropTypes.oneOf(["blue", "green", "red", "orange", "purple", "teal", "grey"]),
  shape: PropTypes.oneOf(["circle", "square"]),
  variant: PropTypes.oneOf(["tinted", "solid"]),
  
};
IconAvatar.defaultProps = { tone: "grey", shape: "circle", variant: "tinted" };

/* -------------------------------------------------------------------------
 * StatusBadge — small pill used in every table (Completed / Accepted /
 * Pending / Active / Failed).
 * ---------------------------------------------------------------------- */

const STATUS_TONE = {
  completed: "green",
  accepted: "green",
  active: "blue",
  pending: "orange",
  failed: "red",
  credit: "green",
  debit: "red",
};

export const StatusBadge = ({ status }) => (
  <span className={`status-badge status-badge--${STATUS_TONE[status.toLowerCase()] || "grey"}`}>
    {status}
  </span>
);

StatusBadge.propTypes = { status: PropTypes.string.isRequired };

/* -------------------------------------------------------------------------
 * PointsValue — colored point delta ("+700 pts" green, "-3500 pts" red,
 * "--" muted when there's nothing to show).
 * ---------------------------------------------------------------------- */

export const PointsValue = ({ value }) => {
  if (value === null || value === undefined) {
    return <span className="points-value points-value--muted">--</span>;
  }
  const positive = value >= 0;
  return (
    <span className={`points-value ${positive ? "points-value--positive" : "points-value--negative"}`}>
      {positive ? "+" : ""}
      {value.toLocaleString()} pts
    </span>
  );
};

PointsValue.propTypes = { value: PropTypes.number };
PointsValue.defaultProps = { value: null };

/* -------------------------------------------------------------------------
 * DataTable — generic table shell used by Profile, Referrals, and Games.
 * Columns declare their own width + custom cell renderer, so each page
 * controls its own content while sharing header/row/stripe styling.
 * ---------------------------------------------------------------------- */

export const DataTable = ({ columns, rows, getRowKey, currentPage, totalPages, onPageChange, loading, loadingMore, onLoadMore, hasMore, emptyState }) => {
  const gridTemplateColumns = columns.map((col) => col.width || "1fr").join(" ");
  const [isMobile, setIsMobile] = useState(false);
  const scrollRef = React.useRef(null);
  const isScrollingRef = React.useRef(false);
  const hasMoreRef = React.useRef(hasMore);

  // Update hasMore ref when prop changes
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 576;
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    if (hasMore && onLoadMore && scrollPercentage >= 0.9 && !isScrollingRef.current) {
      isScrollingRef.current = true;
      onLoadMore();
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 500);
    }
  };

  // Handle window scroll for mobile
  useEffect(() => {
    if (!onLoadMore || !isMobile) {
      return;
    }


    const handleWindowScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;


      if (hasMoreRef.current && !isScrollingRef.current && scrollPercentage >= 0.9) {
        isScrollingRef.current = true;
        onLoadMore();
        setTimeout(() => {
          isScrollingRef.current = false;
        }, 500);
      }
    };

    window.addEventListener('scroll', handleWindowScroll);
    return () => window.removeEventListener('scroll', handleWindowScroll);
  }, [onLoadMore, isMobile]);

  // Render skeleton rows when loading
  const renderSkeletonRows = () => {
    return Array.from({ length: 5 }).map((_, index) => (
      <div
        key={`skeleton-${index}`}
        className="data-table__row data-table__row--skeleton"
        style={{ gridTemplateColumns }}
      >
        {columns.map((col) => (
          <span
            key={col.key}
            className={`data-table__cell data-table__cell--skeleton data-table__cell--${col.align || "left"}`}
          >
            <span className="skeleton-shimmer" />
          </span>
        ))}
      </div>
    ));
  };

  return (
    <div 
      className="data-table" 
      style={{ height: 'calc(100vh - 220px)', maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', overflowX: 'auto' }}
      ref={scrollRef}
      onScroll={onLoadMore ? handleScroll : undefined}
    >
      <div className="data-table__row data-table__row--header" style={{ gridTemplateColumns }}>
        {columns.map((col) => (
          <span
            key={col.key}
            className={`data-table__cell data-table__cell--header data-table__cell--${col.align || "left"} ${col.className || ""}`}
          >
            {col.label}
          </span>
        ))}
      </div>

      <div className="data-table__body">
        {loading ? (
          renderSkeletonRows()
        ) : rows.length === 0 ? (
          emptyState || null
        ) : (
          rows.map((row, index) => (
            <div
              key={getRowKey ? getRowKey(row) : index}
              className="data-table__row"
              style={{ gridTemplateColumns }}
            >
              {columns.map((col) => (
                <span
                  key={col.key}
                  className={`data-table__cell data-table__cell--${col.align || "left"} ${col.className || ""}`}
                >
                  {col.render ? col.render(row) : row[col.key]}
                </span>
              ))}
            </div>
          ))
        )}
        {(hasMore || loadingMore) && !loading && rows.length > 0 && (
          <div className="data-table__loading-more">
            <div className="data-table__loading-spinner" />
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="data-table__pagination">
          <button
            type="button"
            className="data-table__pagination-btn"
            disabled={currentPage === 1}
            onClick={() => onPageChange && onPageChange(currentPage - 1)}
          >
            Previous
          </button>
          <span className="data-table__pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            className="data-table__pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => onPageChange && onPageChange(currentPage + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      width: PropTypes.string,
      align: PropTypes.oneOf(["left", "center", "right"]),
      render: PropTypes.func,
    })
  ).isRequired,
  rows: PropTypes.array.isRequired,
  getRowKey: PropTypes.func,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  onPageChange: PropTypes.func,
  loading: PropTypes.bool,
  loadingMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  hasMore: PropTypes.bool,
};

DataTable.defaultProps = {
  currentPage: 1,
  totalPages: 1,
  onPageChange: null,
  loading: false,
};

/* -------------------------------------------------------------------------
 * StatMiniCard — small tinted stat card (used in a row of 4 on Games).
 * ---------------------------------------------------------------------- */

const TONE_ICONS = {
  blue: gameBlue,
  green: gameGreen,
  red: gameRed,
  purple: gamePurple,
};

export const StatMiniCard = ({ icon, tone, label, value }) => (
  <div className={`stat-mini-card stat-mini-card--${tone}`}>
    <img src={TONE_ICONS[tone] || icon} alt="" className="stat-mini-card__icon" />
    <div>
      <p className="stat-mini-card__label">{label}</p>
      <p className="stat-mini-card__value">{value}</p>
    </div>
  </div>
);

StatMiniCard.propTypes = {
  icon: PropTypes.string.isRequired,
  tone: PropTypes.oneOf(["blue", "green", "red", "purple"]).isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
 
/* -------------------------------------------------------------------------
 * ProgressBar — horizontal progress bar for challenge cards
 * ---------------------------------------------------------------------- */

export const ProgressBar = ({ value, max, color = "#ec421c", label = "" }) => (
  <div className="progress-bar">
    <div className="progress-bar__row">
      <span className="progress-bar__label">{label}</span>
      <span className="progress-bar__value">{value} / {max}</span>
    </div>
    <div className="progress-bar__track">
      <div 
        className="progress-bar__fill" 
        style={{ 
          width: `${(value / max) * 100}%`, 
          backgroundColor: color
        }} 
      />
    </div>
  </div>
);

ProgressBar.propTypes = {
  value: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  color: PropTypes.string,
  label: PropTypes.string,
};

/* -------------------------------------------------------------------------
 * StepChips — equal-width week/milestone chips (done = check, pending =
 * flame), used by challenge cards like "4-Week Dining Streak".
 * ---------------------------------------------------------------------- */
 
export const StepChips = ({ steps, pendingIcon, className = "" }) => (
  <div 
    className={`step-chips step-chips--${steps.length} ${className}`} 
    style={{ gridTemplateColumns: `repeat(${steps.length}, 1fr)` }}
  >
    {steps.map((step) => (
      <div key={step.label} className={`step-chip ${step.done ? "step-chip--done" : ""}`}>
        <span className="step-chip__icon">
          {step.done ? (
            <img src={checkIcon} alt="" />
          ) : (
            <img src={pendingIcon} alt="" className="step-chip__icon--plain" />
          )}
        </span>
        <span className="step-chip__label">{step.label}</span>
      </div>
    ))}
  </div>
);
 
StepChips.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string.isRequired, done: PropTypes.bool })
  ).isRequired,
  pendingIcon: PropTypes.string.isRequired,
  className: PropTypes.string,
};
 
/* -------------------------------------------------------------------------
 * StepNodes — connected circular milestone timeline (done = filled check,
 * pending = empty ring), used by challenge cards like "Weekend Explorer".
 * ---------------------------------------------------------------------- */
 
export const StepNodes = ({ nodes }) => (
  <div className="step-nodes">
    {nodes.map((node, index) => (
      <React.Fragment key={node.label}>
        <div className="step-node">
          <span className={`step-node__circle ${node.done ? "step-node__circle--done" : ""}`}>
            {node.done && <img src={checkIcon} alt="" />}
          </span>
          <span className="step-node__label">{node.label}</span>
        </div>
        {index < nodes.length - 1 && (
          <div className={`step-node__connector ${node.done ? "step-node__connector--done" : ""}`} />
        )}
      </React.Fragment>
    ))}
  </div>
);
 
StepNodes.propTypes = {
  nodes: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string.isRequired, done: PropTypes.bool })
  ).isRequired,
};
 
/* -------------------------------------------------------------------------
 * ChallengeCard — gradient banner card used on Games (and reusable
 * anywhere a "challenge / streak" needs displaying). Supports either a
 * StepChips or StepNodes progress visual via `progress`.
 * ---------------------------------------------------------------------- */
 
export const ChallengeCard = ({
  tone,
  title,
  badge,
  description,
  progress,
  footerIcon,
  footerLeft,
  footerRight,
}) => (
  <div className={`challenge-card challenge-card--${tone}`}>
    <div className="challenge-card__top">
      <h3 className="challenge-card__title">{title}</h3>
      {badge && <span className="challenge-card__badge">{badge}</span>}
    </div>
    <p className="challenge-card__description">{description}</p>
 
    <div className="challenge-card__progress">{progress}</div>
 
    <div className="challenge-card__footer">
      <span className="challenge-card__footer-left">
        {footerIcon && <img src={footerIcon} alt="" />}
        {footerLeft}
      </span>
      <span className="challenge-card__footer-right">{footerRight}</span>
    </div>
  </div>
);
 
ChallengeCard.propTypes = {
  tone: PropTypes.oneOf(["orange", "pink"]).isRequired,
  title: PropTypes.string.isRequired,
  badge: PropTypes.string,
  description: PropTypes.string.isRequired,
  progress: PropTypes.node.isRequired,
  footerIcon: PropTypes.string,
  footerLeft: PropTypes.string.isRequired,
  footerRight: PropTypes.string.isRequired,
};
ChallengeCard.defaultProps = { badge: null, footerIcon: null };


/* -------------------------------------------------------------------------
 * FaqAccordion — expandable FAQ list.
 * ---------------------------------------------------------------------- */

export const FaqAccordion = ({ items, loadingMore, onLoadMore, hasMore }) => {
  const [openId, setOpenId] = useState(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollRef = React.useRef(null);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    if (hasMore && onLoadMore && scrollPercentage >= 0.9 && !isScrolling) {
      setIsScrolling(true);
      onLoadMore();
      setTimeout(() => setIsScrolling(false), 500);
    }
  };

  return (
    <div 
      className="faq-accordion" 
      ref={scrollRef}
      onScroll={onLoadMore ? handleScroll : undefined}
      style={onLoadMore ? { maxHeight: "500px", overflowY: "auto" } : undefined}
    >
      {items.map((item) => {
        const isOpen = openId === item.id;
        return (
          <div key={item.id} className="faq-item">
            <button
              type="button"
              className="faq-item__question"
              onClick={() => setOpenId(isOpen ? null : item.id)}
              aria-expanded={isOpen}
            >
              {item.question}
              <img
                src={chevronDownIcon}
                alt=""
                className={`faq-item__chevron ${isOpen ? "faq-item__chevron--open" : ""}`}
              />
            </button>
            {isOpen && <p className="faq-item__answer">{item.answer}</p>}
          </div>
        );
      })}
      {(hasMore || loadingMore) && items.length > 0 && (
        <div className="faq-accordion__loading-more">
          <span className="skeleton-shimmer skeleton-shimmer--small" />
        </div>
      )}
    </div>
  );
};

FaqAccordion.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      question: PropTypes.string.isRequired,
      answer: PropTypes.string.isRequired,
    })
  ).isRequired,
  loadingMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  hasMore: PropTypes.bool,
};

FaqAccordion.defaultProps = {
  loadingMore: false,
  onLoadMore: null,
  hasMore: false,
};

/* -------------------------------------------------------------------------
 * SidePanelCard — generic titled card wrapper for sidebar content.
 * ---------------------------------------------------------------------- */

export const SidePanelCard = ({ eyebrow, title, children }) => (
  <div className="side-panel-card">
    {eyebrow && <p className="side-panel-card__eyebrow">{eyebrow}</p>}
    {title && <h2 className="side-panel-card__title">{title}</h2>}
    {children}
  </div>
);

SidePanelCard.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
};
SidePanelCard.defaultProps = { eyebrow: null, title: null };

/* -------------------------------------------------------------------------
 * LinkRow — "Terms of Service ->" style row (Support's Legal & Resources).
 * ---------------------------------------------------------------------- */

export const LinkRow = ({ label, onClick }) => (
  <button type="button" className="link-row" onClick={onClick}>
    <span>{label}</span>
    <img src={arrowRightIconAuth} alt="" className="link-row__icon" />
  </button>
);

LinkRow.propTypes = { label: PropTypes.string.isRequired, onClick: PropTypes.func };
LinkRow.defaultProps = { onClick: () => {} };

/* -------------------------------------------------------------------------
 * GuideSteps — numbered blue-square step list (Referrals "How to Use").
 * Visually distinct from the orange-circle NumberedSteps used in modals.
 * ---------------------------------------------------------------------- */

export const GuideSteps = ({ steps }) => (
  <ol className="guide-steps">
    {steps.map((step, index) => (
      <li key={step} className="guide-step">
        <span className="guide-step__num">{index + 1}</span>
        <span className="guide-step__text">{step}</span>
      </li>
    ))}
  </ol>
);

GuideSteps.propTypes = { steps: PropTypes.arrayOf(PropTypes.string).isRequired };

/* -------------------------------------------------------------------------
 * ToggleRow — labeled row with a toggle switch (Settings notifications).
 * ---------------------------------------------------------------------- */

export const ToggleRow = ({ label, checked, onChange, style }) => (
  <div className="toggle-row" style={style}>
    <span className="toggle-row__label">{label}</span>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className={`toggle-switch ${checked ? "toggle-switch--on" : ""}`}
      onClick={() => onChange(!checked)}
    >
      <span className="toggle-switch__thumb" />
    </button>
  </div>
);

ToggleRow.propTypes = {
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  style: PropTypes.object,
};

ToggleRow.defaultProps = {
  style: {},
};

/* -------------------------------------------------------------------------
 * AvatarUpload — profile photo with optional edit/delete controls.
 * ---------------------------------------------------------------------- */

export const AvatarUpload = ({ src, editable, onEdit, onDelete, editIcon, deleteIcon }) => (
  <div className="avatar-upload">
    <img src={src} alt="Profile" className="avatar-upload__img" />
    {editable && (
      <div className="avatar-upload__controls">
        <button type="button" className="avatar-upload__btn" onClick={onEdit} aria-label="Edit photo">
          <img src={editIcon} alt="" />
        </button>
        <div className="avatar-upload__divider"></div>
        <button
          type="button"
          className="avatar-upload__btn avatar-upload__btn--danger"
          onClick={onDelete}
          aria-label="Remove photo"
        >
          <img src={deleteIcon} alt="" />
        </button>
      </div>
    )}
  </div>
);

AvatarUpload.propTypes = {
  src: PropTypes.string.isRequired,
  editable: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  editIcon: PropTypes.string,
  deleteIcon: PropTypes.string,
};
AvatarUpload.defaultProps = {
  editable: false,
  onEdit: () => {},
  onDelete: () => {},
  editIcon: null,
  deleteIcon: null,
};

/* -------------------------------------------------------------------------
 * LinkedCardRow — linked payment card summary (Settings' Account
 * Settings), kept generic in case other screens need to show this too.
 * ---------------------------------------------------------------------- */

export const LinkedCardRow = ({ icon, cardName, last4 }) => (
  <div className="linked-card-row">
    
      <img src={icon} alt="" />
    
    <div>
      <p className="linked-card-row__name">{cardName}</p>
      <p className="linked-card-row__digits">&bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull; {last4}</p>
    </div>
  </div>
);

LinkedCardRow.propTypes = {
  icon: PropTypes.string.isRequired,
  cardName: PropTypes.string.isRequired,
  last4: PropTypes.string.isRequired,
};
